import { createFileRoute, useRouter } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useState } from "react";
import { unlockContent } from "@/lib/content-gate.functions";
import { pageSeo } from "@/lib/seo";

export const Route = createFileRoute("/content/unlock")({
  head: () => ({
    ...pageSeo({
      path: "/content/unlock",
      title: "Unlock Content Archive | CRS Energy",
      description: "Password required.",
    }),
    meta: [{ name: "robots", content: "noindex, nofollow" }],
  }),
  component: UnlockPage,
});

function UnlockPage() {
  const router = useRouter();
  const unlock = useServerFn(unlockContent);
  const [password, setPassword] = useState("");
  const [error, setError] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  async function onSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setSubmitting(true);
    setError(false);
    try {
      const { ok } = await unlock({ data: { password } });
      if (ok) {
        await router.navigate({ to: "/content" });
      } else {
        setError(true);
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <section className="container-page max-w-md py-16 md:py-24">
      <h1 className="text-2xl font-bold text-foreground mb-2">Content Archive</h1>
      <p className="text-sm text-muted-foreground mb-6">
        Enter the password to access this section.
      </p>
      <form onSubmit={onSubmit} className="space-y-4">
        <div>
          <label htmlFor="content-password" className="block text-sm font-medium mb-1.5">
            Password
          </label>
          <input
            id="content-password"
            name="password"
            type="password"
            autoComplete="current-password"
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-accent"
          />
        </div>
        {error && (
          <p className="text-sm text-destructive">Incorrect password. Please try again.</p>
        )}
        <button
          type="submit"
          disabled={submitting || !password}
          className="inline-flex items-center justify-center rounded-full bg-accent text-accent-foreground px-6 py-2.5 text-sm font-semibold shadow-cta hover:bg-accent/90 transition-colors disabled:opacity-60"
        >
          {submitting ? "Checking…" : "Enter"}
        </button>
      </form>
    </section>
  );
}
